import { RegionShared } from "@1games-ru/region-shared";
import { InventoryOwnerType } from "@1games-ru/region-shared/dist/enums/inventory/inventory";
import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { union } from "lodash";
import { useCallback, useMemo } from "react";
import formatPrice from "@/core/helpers/formatPrice";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import { CashIcon } from "@/shared/icons";
import { SoundButton } from "@/shared/ui";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  inventoryByIdSelector,
  inventoryMapSelector,
  setMemoizedItem,
} from "@/domains/inventory/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import toast from "@/domains/toasts/helpers/toast";
import useTradeManager from "@/domains/inventory/hooks/trade/useTradeManager";
import useTradeParticipants from "@/domains/inventory/hooks/trade/useTradeParticipants";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useGetInventory from "@/domains/inventory/hooks/useGetInventory";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventorySeparated from "@/domains/inventory/components/InventorySeparated";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";
import InventoryTradeExchangeHeader from "@/domains/inventory/components/trade/InventoryTradeExchangeHeader";

type Props = { currCharacterId: number; inventoryId: number };

export default function InventoryTradeExchangeContainer({ currCharacterId, inventoryId }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();
  const dispatch = useAppDispatch();
  const playerInfo = useAppSelector(playerInfoSelector);
  const { tradeParticipant, trade } = useTradeParticipants(currCharacterId);
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const inventoryMap = useAppSelector(inventoryMapSelector);
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);
  const { addItemToTrade, removeItemFromTrade, moveInventoryItem, cancelTrade } = useTradeManager();
  const { getInventory } = useGetInventory();

  const doubleClickItemHandler = useCallback((item: InventoryItem) => {
    switch (item.itemTemplate.type) {
      case OwnerType.Bag: {
        getInventory({
          inventoryOwnerId: item.id,
          inventoryOwnerType: InventoryOwnerType.Bag,
        });
        return;
      }
      default:
        return;
    }
  }, []);

  const onClose = useCallback(() => {
    cancelTrade({ tradeId: Number(trade.tradeId) });
  }, [cancelTrade, trade]);

  const disabledItemsByFilter = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  const disabledItemsByConfiscation = useMemo(() => {
    if (tradeParticipant?.self && inventory && inventory?.items) {
      const notTradableItems = inventory.items
        .filter((item) => !item.itemTemplate.isTradable)
        .map((item) => item.id);
      const exchangeItems = tradeParticipant.self.inventory.items.map((item) => item.id);
      return [...notTradableItems, ...exchangeItems];
    }

    return [];
  }, [tradeParticipant, trade]);

  const disabledItems = useMemo(
    () => union(disabledItemsByConfiscation, disabledItemsByFilter),
    [disabledItemsByConfiscation, disabledItemsByFilter],
  );

  const putAll = useCallback(async () => {
    const response = await RpcMpProvider.callServer(
      RegionShared.Backend.Events.Inventory.TradePutAllItemsFromInventoryToTrade,
      {
        fromInventoryId: inventoryId,
      },
    );

    if (response instanceof RpcMpProviderSuccessResponse) {
      const characterInventory = Object.values(inventoryMap).find(
        (item) => item.ownerType === InventoryOwnerType.Character,
      );
      if (characterInventory) {
        getInventory({
          inventoryOwnerId: characterInventory.ownerId,
          inventoryOwnerType: InventoryOwnerType.Character,
        });
      }
    } else {
      toast.error(response.message);
      console.log({ response });
    }
  }, [inventoryId, inventoryMap]);

  if (!inventory) return null;

  return (
    <InventoryWrapper className="max-h-[42rem]">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventoryTradeExchangeHeader onClose={onClose} />
      </InventoryHeader>

      <InventoryContent className="flex items-center justify-between py-5">
        <div className="text-18 font-medium">Инвентарь</div>
        <div className="flex items-center gap-5">
          <InventorySearch value={deferredSearchValue} onChange={onChangeSearchValue} />
          {filterGroups.length > 0 && (
            <>
              <InventorySeparated />
              <InventoryFilter
                groups={filterGroups}
                selectedGroups={selectedGroups}
                onSelect={onSelectHandler}
              />
            </>
          )}
        </div>
      </InventoryContent>

      <InventoryContent className="flex-1 overflow-clip overflow-y-auto py-6">
        <InventoryGrid
          tradeId={trade.tradeId}
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          items={inventory.items}
          onMove={moveInventoryItem}
          onRemoveItemFromTrade={removeItemFromTrade}
          onMemoizedItem={(item) => dispatch(setMemoizedItem(item))}
          onDoubleClickItem={doubleClickItemHandler}
          disabled={tradeParticipant.self.isReady}
          disabledItems={disabledItems}
          onContextMenu={handleContextMenu}
        />
        {itemForContextMenu && isOpen && (
          <InventoryContextMenu
            ref={menuRef}
            position={position}
            item={itemForContextMenu}
            onTrade={() => {
              addItemToTrade(inventory.id, 0, {
                inventoryItemId: itemForContextMenu.id,
                tradeId: trade.tradeId,
              });
            }}
            onClose={closeContextMenu}
          />
        )}
      </InventoryContent>
      <InventoryContent className="py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <CashIcon size={18} />
          <div className="text-14 font-bold text-white/50">
            Наличные:{" "}
            <span className="text-15 font-bold text-white ml-1">
              {formatPrice(Number(playerInfo.character.money.balance.toFixed(3)))} ₽
            </span>
          </div>
        </div>
      </InventoryContent>
      <InventoryFooter>
        <InventoryWeight weight={inventory.weight} capacityWeight={inventory.maxWeight} />
        <SoundButton color="gray" variant="soft" className="h-[2.375rem]" onClick={putAll}>
          Выложить все предметы
        </SoundButton>
      </InventoryFooter>
    </InventoryWrapper>
  );
}
